/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiSelectableOption,
  EuiIcon,
  EuiSelectable,
  EuiPopoverTitle,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButtonGroup,
} from "@inscopix/ideas-eui";
import { ProjectFolderDjango } from "pages/gdt/folders/ProjectFoldersProvider.api";
import { DataTableStoreState } from "pages/gdt/store/DataTableProvider.types";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { useMemo, useState } from "react";
import { TABLE_ICONS } from "types/Icons";

interface ProjectBreadcrumbsTableProps {
  selectedTable?: DataTableStoreState["tables"][number];
  tables: DataTableStoreState["tables"][number][];
  folders: ProjectFolderDjango[];
  projectKey: string;
  tenantKey: string;
}

type Option = EuiSelectableOption<{ tableKey: string | undefined }>;
/*
 * Displays a popover to search and navigate to a project table
 */
export const ProjectBreadcrumbsTablePopover = ({
  selectedTable,
  tables,
  folders,
  projectKey,
  tenantKey,
}: ProjectBreadcrumbsTableProps) => {
  // track search state
  // this is used to remove the folder names from the search results when searching
  // because of the visual awkwardness of the group labels
  const [isSearching, setIsSearching] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const navigateToTable = useRouteMapContext(
    (s) => (tableKey: string) =>
      s.routeMap["PROJECT_TABLE"]
        .dynamicPath({ projectKey, tableKey, tenantKey })
        .navigateTo(),
  );
  // build option list
  const options = useMemo<Option[]>(() => {
    const options: Option[] = [];
    const tableItems: Option[] = tables
      .filter((table) => table.folder === null)
      .filter(({ hidden }) => showHidden || !hidden) // filter out hidden tables
      .map((table) => ({
        label: table.name,
        tableKey: table.key,
        key: table.id,
        checked: table.id === selectedTable?.id ? "on" : undefined,
        prepend: <EuiIcon type={TABLE_ICONS[table.kind]} />,
      }));
    for (const folder of folders) {
      const tablesInFolder = tables.filter(
        (table) => table.folder === folder.id,
      );
      if (tablesInFolder.length > 0) {
        options.push(
          {
            label: folder.name,
            key: folder.id,
            isGroupLabel: true,
            // no table for group label
            tableKey: undefined,
            // show folder color if available
            css:
              folder.color !== null
                ? css`
                    color: ${folder.color}!important;
                    padding-left: 36px !important;
                  `
                : css`
                    padding-left: 36px !important;
                  `,
            // remove folders from view on search
            // has to be a space character, empty gets ignored
            searchableLabel: " ",
          },
          ...tablesInFolder
            .filter(({ hidden }) => showHidden || !hidden) // filter out hidden tables
            .map((table) => ({
              label: `${table.name} (${table.key})`,
              key: table.id,
              tableKey: table.key,
              searchableLabel: `${table.name} ${table.key}`,
              checked:
                table.id === selectedTable?.id ? ("on" as const) : undefined,
              prepend: (
                <EuiIcon
                  type={TABLE_ICONS[table.kind]}
                  // indent under folders if folders are displayed (not searching)
                  css={isSearching ? {} : { marginLeft: "20px" }}
                />
              ),
            })),
        );
      }
    }
    options.push(...tableItems);
    return options;
  }, [tables, showHidden, selectedTable?.id, folders, isSearching]);

  return (
    <EuiSelectable<Option>
      singleSelection="always"
      options={options}
      searchable
      onChange={(options) => {
        const selectedTableKey = options.find(
          (option) => option.checked === "on",
        )?.tableKey;
        if (selectedTableKey !== undefined) {
          navigateToTable(selectedTableKey);
        }
      }}
      searchProps={{
        placeholder: "Filter tables",
        compressed: true,
        onChange: (searchValue: string) => {
          setIsSearching(searchValue.length > 0);
        },
      }}
      aria-label="Switch to table"
      emptyMessage="No tables available"
      noMatchesMessage="No tables found"
      css={css`
        width: 420px;
      `}
    >
      {(list, search) => (
        <>
          <EuiPopoverTitle paddingSize="s">
            <EuiFlexGroup responsive={false}>
              <EuiFlexItem>{search}</EuiFlexItem>
              <EuiFlexItem
                grow={false}
                css={
                  // fix EUI border radius for single button
                  css`
                    .euiButtonGroupButton {
                      border-radius: 4px !important;
                    }
                  `
                }
              >
                <EuiButtonGroup
                  type="multi"
                  legend="Show/hide hidden tables"
                  options={[
                    {
                      id: "showHidden" as const,
                      label: "Show/hide hidden tables",
                      iconType: "eyeClosed",
                    },
                  ]}
                  idToSelectedMap={{
                    showHidden: showHidden,
                  }}
                  onChange={() => {
                    setShowHidden(!showHidden);
                  }}
                  isIconOnly
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPopoverTitle>
          {list}
        </>
      )}
    </EuiSelectable>
  );
};
